import {
  AgxBodyText,
  AgxButton,
  AgxColumn,
  AgxHeader,
  Images,
  AgxSlideUpModal,
  DocumentTypes,
  AgxRow,
  AgxSparkleForm,
  SPECIAL_CHARACTERS,
  FormConfigType,
  CampaignDetailModel,
  ContractDetailModel,
  StakeholderType,
} from '@urbanx/agx-ui-components';
import dayjs from 'dayjs';
import { useContext, useState } from 'react';
import { isEqual } from 'lodash-es';
import { useAppSelector } from 'hooks/useAppSelector';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { cleanTwoLineAddress } from 'utils/formatAddress';
import { AuthContext } from '@urbanx/agx.auth.client';
import { selectContract } from 'features/campaigns/campaignsReducer';
import { Tabs } from 'types/Tabs';
import { Breakpoints, ScreenSize } from 'utils/screen';
import { setAndShowErrorToast } from 'store/config';
import FormDetailsHeader from '../../Common/FormDetailsHeader';
import useStateFormLabels from 'features/form/FormLabels/useStateFormLabels';
import { getAllBuyerNames, prepareSignatories } from 'utils/vendorBuyerUtil';
import { formatCurrencyWithConditionalDecimals } from 'utils/formatNumber';
import { formatListOfStrings } from 'utils/formatString';
import { completeCampaignStep } from 'Api/Campaigns/campaignsApi';
import { useFormLoader } from 'hooks/useFormLoader';
import { FormType } from '@urbanx/agx.campaigns.client';

interface ResumeContractRequestProps {
  campaign: CampaignDetailModel;
  contract: ContractDetailModel;
  currentTab: Tabs;
  mobile?: boolean;
}

const ResumeContractRequest = ({
  campaign,
  contract,
  currentTab,
  mobile = false,
}: ResumeContractRequestProps) => {
  const { address, campaignId } = campaign;
  const { contractId, lastUpdate, documents, buyers, salePrice } = contract;

  const lastUpdatedOn = `Last updated ${dayjs(lastUpdate).format('DD/MM/YY')}`;

  const allBuyers = prepareSignatories(buyers, StakeholderType.Buyer);
  const allBuyerNames = getAllBuyerNames(allBuyers);

  const [addressLineOne, addressLineTwo] = cleanTwoLineAddress(address);

  const dispatch = useAppDispatch();
  const { getAuthToken } = useContext(AuthContext);
  const { loadForm, loadFormWithConfig } = useFormLoader();

  const availableForms = useAppSelector(state => state.forms.availableForms);

  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [disableButtons, setDisableButtons] = useState(false);
  const isMobile = ScreenSize() === Breakpoints.Mobile;

  const formName = useStateFormLabels(campaign.state, 'formName');

  const contractIsGenerated = documents?.some(
    c => c.documentType === DocumentTypes.SalesContract
  );

  if (!availableForms) return;

  const resumeContract = async () => {
    try {
      setDisableButtons(true);

      await loadForm(campaignId, contractId, FormConfigType.Campaign);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to resume Contract'));
    }

    setDisableButtons(false);
  };

  const beginContractSubmission = async () => {
    const contractSubmissionForm = availableForms.find(
      form =>
        form.type === FormType.ContractSubmission &&
        isEqual(form.state, campaign.state)
    );

    if (!contractSubmissionForm) return;

    try {
      setDisableButtons(true);

      const authToken = await getAuthToken();

      if (!authToken) return;

      const newForm = await completeCampaignStep(
        authToken,
        campaignId,
        true,
        contract.contractId
      );

      if (!newForm) {
        dispatch(setAndShowErrorToast('Failed to start Contract submission'));
        setDisableButtons(false);
        return;
      }

      await loadFormWithConfig(newForm);
    } catch (err) {
      console.error(err);
      dispatch(setAndShowErrorToast('Failed to start Contract submission'));
    }

    setDisableButtons(false);
  };

  const confirmationDialogBody = (
    <AgxColumn veryLargeGap extraClasses={'confirmationModalStyle'}>
      <Images.AlertCircleOutline />
      <AgxHeader size={3}>Starting a submission?</AgxHeader>
      <AgxBodyText medium>
        You will not be able to edit the {formName} once you continue.
      </AgxBodyText>
      <AgxBodyText medium>Continue to submission?</AgxBodyText>
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="startsubmission"
          large
          primary
          text="Yes, start submission"
          onClick={beginContractSubmission}
          disabled={
            disableButtons ||
            (contract?.inProgressForm != null && currentTab === Tabs.Archive)
          }
        />
        <AgxButton
          id="cancel"
          hollow
          large
          text="No, go back"
          onClick={() => setShowConfirmationModel(false)}
          disabled={disableButtons}
        />
      </div>
    </AgxColumn>
  );

  return (
    <>
      <FormDetailsHeader
        id={'resume-contract-header'}
        key={'resume-contract-header'}
        currentTab={currentTab}
        formName="Contract"
        detail={lastUpdatedOn}
        onBack={() => dispatch(selectContract(null))}
      />
      <AgxColumn centered veryLargeGap extraClasses="resumeFormDetails">
        <AgxHeader size={2} centered>
          {addressLineOne},
          <br />
          {addressLineTwo}
        </AgxHeader>
        <AgxBodyText medium centered>
          {formatListOfStrings(allBuyerNames)} {SPECIAL_CHARACTERS.BULLET}{' '}
          {formatCurrencyWithConditionalDecimals(salePrice ?? 0, 2)}
        </AgxBodyText>
        <AgxColumn mediumGap centered>
          {!contractIsGenerated && (
            <AgxColumn smallGap centered>
              <AgxBodyText medium centered>
                There are more fields to fill,
              </AgxBodyText>
              <AgxBodyText medium centered>
                let's continue from where you left off
              </AgxBodyText>
            </AgxColumn>
          )}
        </AgxColumn>
        <AgxSparkleForm
          width={isMobile ? 150 : 200}
          height={isMobile ? 150 : 200}
        />
      </AgxColumn>
      <AgxRow fill justifyCenter>
        <AgxColumn mediumGap>
          <AgxButton
            id="btn-resumeContract"
            primary={contractIsGenerated}
            hollow={!contractIsGenerated}
            wide={mobile}
            large
            text="Resume Contract"
            onClick={resumeContract}
            disabled={disableButtons}
          />
          {!!contract.envelopeSubmission && contractIsGenerated && (
            <AgxButton
              id="btn-startSubmission"
              hollow
              wide={mobile}
              large
              disabled={disableButtons || currentTab === Tabs.Archive}
              text="Start Submission"
              onClick={() => setShowConfirmationModel(true)}
            />
          )}
        </AgxColumn>
      </AgxRow>
      {showConfirmationModel && (
        <AgxSlideUpModal
          closeButton
          content={confirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowConfirmationModel(false)}
        />
      )}
    </>
  );
};

export default ResumeContractRequest;
